import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { routerNavigatedAction } from '@ngrx/router-store'
import { Store } from '@ngrx/store'
import { findLast as _findLast, last as _last } from 'lodash/fp'

import {
  EMPTY,
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  map,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs'

import {
  ActionAgent,
  ActionTarget,
  ConversationOutcomeState,
  ConversationOutcomeType,
  ConversationThread,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { Conversation } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AgentName } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { isFinishedThreadState } from '@flowx/designer/modules/ai/conversation/conversation.utils'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { CustomComponentsApiService } from '@flowx/designer/shared/api/custom-components.api'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import { ReusableFunctionApiService } from '@flowx/designer/shared/api/reusable-functions.api'
import { ReusableTemplatesApiService } from '@flowx/designer/shared/api/reusable-templates.api'
import { AppNavigationService } from '@flowx/designer/shared/app-navigation/app-navigation.service'
import {
  getNavigationTarget,
  getTargetNode,
  isTargetAvailable,
} from '@flowx/designer/shared/app-navigation/navigation-utils'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

const PROCESS_NODE_BOUND_TARGET_TYPES: ReadonlySet<ConversationThreadExecutionPlanActionTargetType> =
  new Set([
    ConversationThreadExecutionPlanActionTargetType.Node,
    ConversationThreadExecutionPlanActionTargetType.BusinessRule,
    ConversationThreadExecutionPlanActionTargetType.Expression,
  ])

@Injectable()
export class ConversationNavigationEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiConversationService = inject(AiConversationService)
  #appNavigationService = inject(AppNavigationService)
  #nodeApiService = inject(NodeApiService)
  #customComponentsApi = inject(CustomComponentsApiService)
  #reusableFunctionsApi = inject(ReusableFunctionApiService)
  #reusableTemplatesApi = inject(ReusableTemplatesApiService)

  #targetExtraResolvers: Partial<
    Record<
      ConversationThreadExecutionPlanActionTargetType,
      (target: ActionTarget) => Observable<TargetNavigationExtras>
    >
  > = {
    [ConversationThreadExecutionPlanActionTargetType.CustomComponent]: (target) =>
      this.#customComponentsApi
        .findByUniqueIdentifier(target.customComponent?.identifier ?? '')
        .pipe(
          map((component) => ({ customComponentResourceId: component?.resourceId })),
          catchError(() => of({} as TargetNavigationExtras))
        ),
    [ConversationThreadExecutionPlanActionTargetType.ReusableFunction]: (target) =>
      this.#reusableFunctionsApi.getAllReusableFunctions().pipe(
        map((response) => {
          const fn = response.resourceContent?.content?.find((f) => f.name === target.name)
          return { reusableFunctionResourceId: fn?.resourceId }
        }),
        catchError(() => of({} as TargetNavigationExtras))
      ),
    [ConversationThreadExecutionPlanActionTargetType.ReusableUiTemplate]: (target) =>
      this.#reusableTemplatesApi
        .getAllReusableTemplates({ name: target.name, latest: true })
        .pipe(
          map((response) => {
            const template = response.resourceContent?.content?.find((t) => t.name === target.name)
            return { reusableUiTemplateResourceId: template?.resourceId }
          }),
          catchError(() => of({} as TargetNavigationExtras))
        ),
  }

  #targetExtraValidators: Partial<
    Record<
      ConversationThreadExecutionPlanActionTargetType,
      (extras: TargetNavigationExtras) => boolean
    >
  > = {
    [ConversationThreadExecutionPlanActionTargetType.CustomComponent]: (extras) =>
      !!extras.customComponentResourceId,
    [ConversationThreadExecutionPlanActionTargetType.ReusableFunction]: (extras) =>
      !!extras.reusableFunctionResourceId,
    [ConversationThreadExecutionPlanActionTargetType.ReusableUiTemplate]: (extras) =>
      !!extras.reusableUiTemplateResourceId,
  }

  onProcessNavigation = createEffect(() =>
    this.#actions$.pipe(
      ofType(routerNavigatedAction),
      withLatestFrom(this.#store.select(selectProcessResourceId)),
      distinctUntilChanged((previous, current) => previous[1] === current[1]),
      map(([, processResourceId]) =>
        ConversationActions.processNavigated({
          processResourceId: processResourceId ?? null,
        })
      )
    )
  )

  navigateToTarget = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.loadConversationSuccess),
        switchMap(({ conversation }) => {
          const targetType = this.#extractThreadNavigationData(_last(conversation.threads)).target
            ?.targetType

          if (!PROCESS_NODE_BOUND_TARGET_TYPES.has(targetType)) {
            return this.#performNavigationIfRequired(conversation, [], undefined)
          }

          return this.#store.select(selectProcessResourceId).pipe(
            take(1),
            switchMap((currentProcessResourceId) =>
              this.#store.select(nodesSelector).pipe(
                take(1),
                catchError(() => of([])),
                filter((nodes) => this.#isValidNodeState(nodes, currentProcessResourceId)),
                switchMap((nodes) => this.#fetchTemplateConfigsIfNeeded(conversation, nodes))
              )
            ),
            switchMap(({ conversation: c, nodes, templateConfigsMap }) =>
              this.#performNavigationIfRequired(c, nodes, templateConfigsMap)
            )
          )
        })
      ),
    { dispatch: false }
  )

  #isValidNodeState(nodes: NodeDefinition[], currentProcessResourceId: string | null): boolean {
    const processDefinition = ProcessEffector.processDefinition$.getState()

    return (
      nodes &&
      nodes.length > 0 &&
      processDefinition &&
      currentProcessResourceId &&
      nodes[0]?.processId === processDefinition.id &&
      processDefinition.resourceId === currentProcessResourceId
    )
  }

  #fetchTemplateConfigsIfNeeded(
    conversation: Conversation,
    nodes: NodeDefinition[]
  ): Observable<{
    conversation: Conversation
    nodes: NodeDefinition[]
    templateConfigsMap?: Record<number, TemplateConfigInterface>
  }> {
    const lastThread = _last(conversation.threads)
    const message = lastThread?.messages[0]
    const action = message?.executionPlan?.actions[0]

    if (action?.target?.targetType === ConversationThreadExecutionPlanActionTargetType.Expression) {
      return this.#nodeApiService
        .getUserTaskTemplateConfigs(nodes.map((node) => node.navigationAreaId))
        .pipe(
          map((navigationAreaTemplateConfigs) => ({
            conversation,
            nodes,
            templateConfigsMap: this.#buildTemplateConfigsMap(navigationAreaTemplateConfigs),
          })),
          catchError(() => of({ conversation, nodes, templateConfigsMap: undefined }))
        )
    }

    return of({ conversation, nodes, templateConfigsMap: undefined })
  }

  #buildTemplateConfigsMap(navigationAreaTemplateConfigs: any): Record<number, any> {
    const templateConfigsMap: Record<number, any> = {}
    Object.values(navigationAreaTemplateConfigs || {}).forEach((areaConfigs: any) => {
      Object.assign(templateConfigsMap, areaConfigs)
    })
    return templateConfigsMap
  }

  #performNavigationIfRequired(
    conversation: Conversation,
    nodes: NodeDefinition[],
    templateConfigsMap?: Record<number, TemplateConfigInterface>
  ): Observable<void> {
    const lastThread = _last(conversation.threads)

    if (
      isFinishedThreadState(lastThread?.state) ||
      lastThread?.id === conversation.defaultThreadId
    ) {
      return EMPTY
    }

    const navigationData = this.#extractThreadNavigationData(lastThread)

    if (!navigationData.agent) {
      return EMPTY
    }

    if (navigationData.target?.targetType != null) {
      return this.#navigateToValidTarget(navigationData, nodes, templateConfigsMap)
    }

    return EMPTY
  }

  #extractThreadNavigationData(thread: ConversationThread): {
    agent: ActionAgent
    messageWithOutcome: Message
    target: ActionTarget
  } {
    const agent = _findLast(
      (message) =>
        message.executionPlan?.actions[0]?.agent?.agent &&
        message.executionPlan?.actions[0]?.agent?.agent !== AgentName.ConnectedGraph,
      thread?.messages
    )?.executionPlan?.actions[0]?.agent

    const messageWithOutcome = _findLast(
      (message) =>
        message.outcome?.outcomes.filter(
          (outcome) =>
            outcome.outcomeType !== ConversationOutcomeType.Unknown &&
            outcome.state === ConversationOutcomeState.Created
        ).length > 0,
      thread?.messages
    )

    const target = _findLast(
      (message) =>
        message.executionPlan?.actions[0]?.target &&
        message.executionPlan?.actions[0]?.target?.targetType != null,
      thread?.messages
    )?.executionPlan?.actions[0]?.target

    return { agent, messageWithOutcome, target }
  }

  #navigateToValidTarget(
    navigationData: {
      agent: ActionAgent
      messageWithOutcome: Message
      target: ActionTarget
    },
    nodes: NodeDefinition[],
    templateConfigsMap?: Record<number, TemplateConfigInterface>
  ): Observable<void> {
    const { agent, messageWithOutcome, target } = navigationData

    if (!isTargetAvailable(target, nodes, templateConfigsMap)) {
      return this.#aiConversationService.discardThread(
        true,
        'The destination of the pending action was deleted. I cannot restore the last version.'
      )
    }

    if (!messageWithOutcome) {
      return EMPTY
    }

    return this.#resolveTargetExtras(target).pipe(
      switchMap((extras) => {
        if (!this.#hasResolvedRequiredExtras(target, extras)) {
          return this.#aiConversationService.discardThread(
            true,
            'The destination of the pending action was deleted. I cannot restore the last version.'
          )
        }

        const navigationTarget = getNavigationTarget(agent, target)
        if (!navigationTarget) {
          return EMPTY
        }

        const node = getTargetNode({ agent, target }, nodes)
        const navigationPayload = this.#buildNavigationPayload(target, node, extras)

        return from(
          this.#appNavigationService.execute({
            id: navigationTarget,
            data: navigationPayload,
          })
        ).pipe(map(() => void 0))
      })
    )
  }

  #resolveTargetExtras(target: ActionTarget): Observable<TargetNavigationExtras> {
    const resolver = this.#targetExtraResolvers[target?.targetType]
    return resolver ? resolver(target) : of({})
  }

  #hasResolvedRequiredExtras(target: ActionTarget, extras: TargetNavigationExtras): boolean {
    const validator = this.#targetExtraValidators[target?.targetType]
    return validator ? validator(extras) : true
  }

  #buildNavigationPayload(
    target: any,
    node: NodeDefinition | null,
    extras: TargetNavigationExtras
  ): Record<string, any> {
    const appId = this.#store.selectSignal(selectAppId)()
    const appVersionId = this.#store.selectSignal(selectAppVersionId)()
    const selectedProcessResourceId = this.#store.selectSignal(selectProcessResourceId)()

    return {
      nodeId: node?.id,
      appId,
      appVersionUuid: appVersionId,
      processVersionUuid: selectedProcessResourceId,
      processId: selectedProcessResourceId,
      navigationAreaId: node?.navigationAreaId,
      swimlaneId: node?.swimlaneDefinitionId,
      businessRuleIndex: target?.businessRule?.index,
      actionId: target?.businessRule?.actionId,
      showHideExpression:
        target?.expression?.expressionType ===
        ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
      showDisableExpression:
        target?.expression?.expressionType ===
        ConversationThreadExecutionPlanActionTargetExpressionType.Disable,
      templateIds: target?.expression?.elementId,
      ...extras,
    }
  }
}

type TargetNavigationExtras = {
  customComponentResourceId?: string
  reusableFunctionResourceId?: string
  reusableUiTemplateResourceId?: string
}
